import { useState, type ReactNode } from 'react';
import {
  ChevronDown,
  HandHelping,
  MessageCircle,
  Mic2,
  Phone,
  Send,
  StickyNote,
  UserRound,
} from 'lucide-react';
import { WhatsAppIcon } from '@/components/icons/whatsapp-icon';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { formatDisplayPhone, phoneTelHref, whatsappPhoneHref } from '@/lib/phone-utils';
import { cn } from '@/lib/utils';
import { giveAskHeading, type GiveAskItem } from '@/lib/give-ask-api';
import {
  categoryLabel,
  parseCategoryOptions,
  parseDreamReferralList,
  type BusinessCategoryOption,
  type NetworkingDetail,
} from '@/lib/conclave-networking';

export type ConclaveTableMate = {
  registrationId: string;
  name: string;
  isSelf?: boolean;
  email?: string;
  phone?: string | null;
  city?: string | null;
  dreamReferral?: NetworkingDetail[] | string | null;
  connectCategories?: BusinessCategoryOption[] | string[];
  giveBusinessCategories?: BusinessCategoryOption[];
  userId?: string | null;
  image?: string | null;
  intro?: { title?: string | null; body?: string | null } | null;
  giveAskItems?: { gives: GiveAskItem[]; asks: GiveAskItem[] };
};

type SpeakingIntro = { title?: string | null; body?: string | null };

function initials(name: string) {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase() ?? '')
    .join('');
}

type DetailEntry = {
  heading: string;
  category: string;
  phone: string;
  email: string;
  notes: string;
};

function giveAskEntry(item: GiveAskItem): DetailEntry {
  return {
    heading: giveAskHeading(item),
    category: [item.subCategory, item.category].filter(Boolean).join(' · '),
    phone: item.contactPhone ?? '',
    email: item.contactEmail ?? '',
    notes: item.description,
  };
}

function dreamEntry(item: NetworkingDetail): DetailEntry {
  return {
    heading: item.businessName.trim() || item.name.trim(),
    category: [item.subCategory, item.businessCategory].filter(Boolean).join(' · '),
    phone: item.phone.trim(),
    email: item.email.trim(),
    notes: item.notes.trim(),
  };
}

function primaryLine(gives: GiveAskItem[]) {
  const first = gives[0];
  if (!first) return { title: '', subtitle: '' };
  const subtitle = first.subCategory || first.category || first.description.slice(0, 72);
  return { title: giveAskHeading(first), subtitle };
}

function DetailBlock({ label, items }: { label: string; items: DetailEntry[] }) {
  if (!items.length) return null;
  return (
    <div>
      <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{label}</p>
      <ul className="mt-2 space-y-2">
        {items.map((item, i) => (
          <li key={i} className="rounded-xl border border-border bg-muted/30 px-3 py-2 text-sm">
            <p className="font-medium text-foreground">{item.heading || label}</p>
            {item.category ? (
              <p className="text-xs text-muted-foreground">{item.category}</p>
            ) : null}
            {item.phone ? <PhoneContactRow phone={item.phone} className="mt-2" compact /> : null}
            {item.email ? (
              <a
                href={`mailto:${item.email}`}
                className="mt-1 block truncate text-xs text-primary hover:underline"
                onClick={(e) => e.stopPropagation()}
              >
                {item.email}
              </a>
            ) : null}
            {item.notes ? <p className="mt-1 text-xs text-muted-foreground">{item.notes}</p> : null}
          </li>
        ))}
      </ul>
    </div>
  );
}

export function ConclaveTableMateCard({
  mate,
  onGive,
  onAsk,
  onNote,
  onLead,
  isSpeaking = false,
  speakingIntro,
  speakingClock,
  className,
}: {
  mate: ConclaveTableMate;
  onGive?: (mate: ConclaveTableMate) => void;
  onAsk?: (mate: ConclaveTableMate) => void;
  onNote?: (mate: ConclaveTableMate) => void;
  onLead?: (mate: ConclaveTableMate) => void;
  isSpeaking?: boolean;
  speakingIntro?: SpeakingIntro | null;
  speakingClock?: ReactNode;
  className?: string;
}) {
  const [expanded, setExpanded] = useState(false);
  const gives = mate.giveAskItems?.gives ?? [];
  const asks = mate.giveAskItems?.asks ?? [];
  const dream = parseDreamReferralList(mate.dreamReferral).map(dreamEntry);
  const seek = parseCategoryOptions(mate.connectCategories);
  const yours = parseCategoryOptions(mate.giveBusinessCategories);
  const { title, subtitle } = primaryLine(gives);
  const phone = mate.phone?.trim() ?? '';
  const hidden = mate.name === 'Hidden';
  const introTitle =
    speakingIntro?.title?.trim() || mate.intro?.title?.trim() || (isSpeaking ? 'Speaking now' : '');
  const introBody = speakingIntro?.body?.trim() || mate.intro?.body?.trim() || '';
  const showActionChips = (!mate.isSelf && !hidden) || isSpeaking;

  return (
    <article
      className={cn(
        'overflow-hidden rounded-xl border shadow-sm md:rounded-2xl',
        isSpeaking ? 'border-primary/30 bg-primary/10' : 'border-border bg-card',
        mate.isSelf && !isSpeaking && 'ring-2 ring-primary/25',
        className
      )}
    >
      <div className="p-4">
        <div className="flex items-start gap-3">
          <div className="relative shrink-0">
            {isSpeaking ? (
              <span className="relative flex h-16 w-16 items-center justify-center">
                <span
                  aria-hidden
                  className="animate-speaking-ring absolute inset-0 rounded-xl border-2 border-primary"
                />
                <span
                  aria-hidden
                  className="animate-speaking-ring-delayed absolute inset-0 rounded-xl border-2 border-primary/70"
                />
                <Avatar className="relative z-10 h-16 w-16 rounded-xl border-2 border-primary">
                  <AvatarImage
                    src={mate.image ?? undefined}
                    alt={mate.name}
                    className="rounded-xl object-cover"
                  />
                  <AvatarFallback className="rounded-xl bg-gradient-to-br from-primary/15 to-primary/5 text-lg font-semibold text-primary">
                    <UserRound className="h-7 w-7" />
                  </AvatarFallback>
                </Avatar>
              </span>
            ) : (
              <Avatar className="h-16 w-16 shrink-0 rounded-xl border-2 border-primary/40 ring-2 ring-primary/15">
                <AvatarImage
                  src={mate.image ?? undefined}
                  alt={mate.name}
                  className="rounded-xl object-cover"
                />
                <AvatarFallback className="rounded-xl bg-gradient-to-br from-primary/15 to-primary/5 text-lg font-semibold text-primary">
                  <UserRound className="h-7 w-7" />
                </AvatarFallback>
              </Avatar>
            )}
            {isSpeaking ? (
              <span className="absolute -bottom-1 -right-1 z-20 flex h-7 w-7 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-md">
                <Mic2 className="h-3.5 w-3.5 animate-pulse" />
              </span>
            ) : null}
          </div>
          <div className="min-w-0 flex-1">
            {isSpeaking ? (
              <div className="flex items-start justify-between gap-2">
                <p className="pt-1 text-[10px] font-bold uppercase tracking-widest text-primary">
                  Current speaker
                </p>
                {speakingClock}
              </div>
            ) : null}
            <h3
              className={cn(
                'truncate font-semibold text-foreground',
                isSpeaking ? 'text-lg' : 'text-base'
              )}
            >
              {mate.name}
              {mate.isSelf ? (
                <span className="ml-1.5 text-sm font-normal text-primary">(you)</span>
              ) : null}
            </h3>
            {isSpeaking ? (
              <>
                <p className="mt-0.5 font-medium text-foreground/90">{introTitle}</p>
                {introBody ? (
                  <p className="mt-2 line-clamp-4 text-sm leading-relaxed text-muted-foreground">
                    {introBody}
                  </p>
                ) : null}
              </>
            ) : (
              <>
                {title ? (
                  <p className="truncate text-sm font-medium text-foreground/90">{title}</p>
                ) : null}
                {subtitle ? (
                  <p className="mt-0.5 line-clamp-2 text-xs text-muted-foreground">{subtitle}</p>
                ) : null}
              </>
            )}
          </div>
        </div>

        <div className="mt-3">
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
            {mate.city ? <span>{mate.city}</span> : null}
            {phone ? <PhoneContactRow phone={phone} /> : null}
          </div>

          {!isSpeaking && mate.intro?.title && !expanded ? (
            <p className="mt-2 line-clamp-2 text-xs italic text-muted-foreground">
              “{mate.intro.title}”
            </p>
          ) : null}

          {showActionChips ? (
            <div className="mt-3 grid grid-cols-5 gap-1.5">
              <ActionChip
                icon={<HandHelping className="h-4 w-4" />}
                label="Gives"
                onClick={() => onGive?.(mate)}
              />
              <ActionChip
                icon={<MessageCircle className="h-4 w-4" />}
                label="Asks"
                onClick={() => onAsk?.(mate)}
              />
              <ActionChip
                icon={<Send className="h-4 w-4" />}
                label="Lead"
                disabled={!mate.userId || mate.isSelf}
                onClick={() => onLead?.(mate)}
              />
              <ActionChip
                icon={<StickyNote className="h-4 w-4" />}
                label="Notes"
                disabled={mate.isSelf}
                onClick={() => onNote?.(mate)}
              />
              <ActionChip
                icon={
                  <ChevronDown className={cn('h-4 w-4 transition', expanded && 'rotate-180')} />
                }
                label="Details"
                onClick={() => setExpanded((v) => !v)}
              />
            </div>
          ) : (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="mt-2 h-8 rounded-lg px-2 text-xs"
              onClick={() => setExpanded((v) => !v)}
            >
              {expanded ? 'Hide details' : 'View details'}
              <ChevronDown
                className={cn('ml-1 h-3.5 w-3.5 transition', expanded && 'rotate-180')}
              />
            </Button>
          )}
        </div>
      </div>
      {expanded ? (
        <div className="space-y-4 border-t border-border bg-muted/20 px-4 py-4">
          {!isSpeaking && mate.intro?.body ? (
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Speaking intro
              </p>
              <p className="mt-1 text-sm font-medium">{mate.intro.title}</p>
              <p className="text-sm text-muted-foreground">{mate.intro.body}</p>
            </div>
          ) : null}
          <DetailBlock label="Gives" items={gives.map(giveAskEntry)} />
          <DetailBlock label="Asks" items={asks.map(giveAskEntry)} />
          <DetailBlock label="Dream referral" items={dream} />
          {(seek.length > 0 || yours.length > 0) && (
            <div className="flex flex-wrap gap-2">
              {seek.map((c) => (
                <span
                  key={`seek-${categoryLabel(c)}`}
                  className="rounded-full bg-primary/10 px-2.5 py-1 text-[11px] font-medium text-primary"
                >
                  Seek: {categoryLabel(c)}
                </span>
              ))}
              {yours.map((c) => (
                <span
                  key={`yours-${categoryLabel(c)}`}
                  className="rounded-full bg-muted px-2.5 py-1 text-[11px] font-medium text-foreground"
                >
                  Connect: {categoryLabel(c)}
                </span>
              ))}
            </div>
          )}
        </div>
      ) : null}
    </article>
  );
}

function PhoneContactRow({
  phone,
  className,
  compact,
}: {
  phone: string;
  className?: string;
  compact?: boolean;
}) {
  const formatted = formatDisplayPhone(phone);
  const tel = phoneTelHref(phone);
  const whatsapp = whatsappPhoneHref(phone);
  if (!formatted) return null;

  return (
    <div
      className={cn(
        'inline-flex max-w-full items-center gap-2',
        compact ? 'text-xs' : 'rounded-xl border border-border bg-muted/40 px-2.5 py-1.5',
        className
      )}
      onClick={(e) => e.stopPropagation()}
    >
      <a
        href={tel}
        className="inline-flex min-w-0 items-center gap-1.5 font-medium text-foreground hover:text-primary"
      >
        <Phone className="h-3.5 w-3.5 shrink-0 text-primary" />
        <span className="truncate tracking-wide">{formatted}</span>
      </a>
      {whatsapp ? (
        <a
          href={whatsapp}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-[#25D366]/15 text-[#128C7E] hover:bg-[#25D366] hover:text-white transition-colors"
          title="WhatsApp"
        >
          <WhatsAppIcon className="h-[18px] w-[18px]" />
        </a>
      ) : null}
    </div>
  );
}

function ActionChip({
  icon,
  label,
  onClick,
  disabled,
}: {
  icon: ReactNode;
  label: string;
  onClick?: () => void;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className="flex flex-col items-center gap-1 rounded-xl border border-border bg-background px-1 py-2 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground transition hover:border-primary/40 hover:bg-primary/5 hover:text-primary disabled:pointer-events-none disabled:opacity-40"
    >
      {icon}
      {label}
    </button>
  );
}

export function ConclaveTableMateStrip({
  mates,
  activeId,
  onSelect,
}: {
  mates: ConclaveTableMate[];
  activeId?: string | null;
  onSelect?: (mate: ConclaveTableMate) => void;
}) {
  if (mates.length === 0) return null;
  return (
    <div className="flex gap-3 overflow-x-auto pb-1 no-scrollbar">
      {mates.map((mate) => (
        <button
          key={mate.registrationId}
          type="button"
          onClick={() => onSelect?.(mate)}
          className={cn(
            'flex w-[4.5rem] shrink-0 flex-col items-center gap-1 rounded-xl p-1 transition',
            activeId === mate.registrationId && 'bg-primary/10'
          )}
        >
          <Avatar className="h-12 w-12 rounded-full border-2 border-background shadow-sm">
            <AvatarFallback
              className={cn(
                'rounded-full text-sm font-semibold',
                mate.isSelf ? 'bg-primary text-primary-foreground' : 'bg-muted text-foreground'
              )}
            >
              {initials(mate.name) || '?'}
            </AvatarFallback>
          </Avatar>
          <span className="w-full truncate text-center text-[11px] font-medium leading-tight">
            {mate.name.split(' ')[0]}
          </span>
        </button>
      ))}
    </div>
  );
}
